import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getUserInfo, getDairyInfo, getWalletBalance, getCurrentMarketPrice, updateMarketPrice, updateDairyInfo } from '../services/api';
import { removeToken } from '../services/tokenStorage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSignOutAlt, 
  faPlus, 
  faListAlt, 
  faBoxOpen, 
  faFileAlt,
  faStore,
  faUser,
  faPhone,
  faWallet,
  faEdit,
  faCheck,
  faSpinner,
  faExchangeAlt,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import './Dashboard.css';

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dairyInfo, setDairyInfo] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [currentRate, setCurrentRate] = useState(null);
  const [isLoadingRate, setIsLoadingRate] = useState(true);
  const [isEditingRate, setIsEditingRate] = useState(false);
  const [customRate, setCustomRate] = useState('');
  const [isEditingRateType, setIsEditingRateType] = useState(false);
  const [rateType, setRateType] = useState('fat_snf'); // Default to fat_snf
  const [submitting, setSubmitting] = useState(false);
  const [submittingRateType, setSubmittingRateType] = useState(false);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [showLowBalanceAlert, setShowLowBalanceAlert] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [currentSubmitMessage, setCurrentSubmitMessage] = useState('');
  
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    // Remove token and redirect to login
    removeToken();
    navigate('/login');
  }, [navigate]);

  useEffect(() => {
    // Fetch user info when component mounts
    const fetchUserInfo = async () => {
      try {
        const response = await getUserInfo();
        setUser(response);
      } catch (error) {
        console.error('Error fetching user info:', error);
        // If there's an error fetching user info, logout
        handleLogout();
      } finally {
        setLoading(false);
      }
    };

    fetchUserInfo();
  }, [handleLogout]);

  // Fetch dairy information, wallet balance, and current rate
  useEffect(() => {
    const fetchDairyAndRateInfo = async () => {
      try {
        setLoadingInfo(true);
        
        // Fetch dairy information
        const dairyResponse = await getDairyInfo();
        setDairyInfo(dairyResponse);
        
        // Set the rate type from dairy info if available
        if (dairyResponse && dairyResponse.rate_type) {
          setRateType(dairyResponse.rate_type);
        }
        
        // Fetch wallet balance
        try {
          const walletResponse = await getWalletBalance();
          console.log('Wallet API response:', walletResponse);
          
          let balance = null;
          
          // Check different possible properties for balance
          if (walletResponse && walletResponse.balance !== undefined) {
            balance = walletResponse.balance;
          } else if (walletResponse && walletResponse.amount !== undefined) {
            balance = walletResponse.amount;
          } else if (walletResponse && typeof walletResponse === 'number') {
            // In case the response is just the balance as a number
            balance = walletResponse;
          } else if (walletResponse && typeof walletResponse === 'object') {
            // Log all available properties
            console.log('Available wallet properties:', Object.keys(walletResponse));
            // Try to find a property that might contain the balance
            const possibleBalanceKeys = ['balance', 'amount', 'wallet_balance', 'value', 'total'];
            for (const key of possibleBalanceKeys) {
              if (walletResponse[key] !== undefined) {
                console.log(`Found balance in property: ${key} with value: ${walletResponse[key]}`);
                balance = walletResponse[key];
                break;
              }
            }
          }
          
          setWalletBalance(balance);
          
          // Check if balance is below threshold (50 rupees)
          if (balance !== null && balance !== undefined && parseFloat(balance) < 50) {
            setShowLowBalanceAlert(true);
          }
        } catch (error) {
          console.error('Error fetching wallet balance:', error);
        }
        
        // Fetch current market price
        try {
          setIsLoadingRate(true);
          const response = await getCurrentMarketPrice();
          if (response && response.price) {
            setCurrentRate(response.price);
            setCustomRate(response.price.toString()); // Initialize custom rate with current rate
          }
        } catch (error) {
          setCurrentRate(0);
          setCustomRate('0');
          console.error('Error fetching current rate:', error);
        } finally {
          setIsLoadingRate(false);
        }
      } catch (error) {
        console.error('Error fetching dairy information or wallet balance:', error);
      } finally {
        setLoadingInfo(false);
      }
    };
    
    fetchDairyAndRateInfo();
  }, []);

  // Handle rate edit toggle
  const toggleRateEdit = async () => {
    if (isEditingRate) {
      // Save the edited rate if it's valid
      const parsedRate = parseFloat(customRate);
      if (!isNaN(parsedRate) && parsedRate >= 0) {
        try {
          setSubmitting(true);
          // Call API to update the rate in the backend
          await updateMarketPrice(parsedRate);
          setCurrentRate(parsedRate);
          
          // Show success message
          setCurrentSubmitMessage('Milk rate updated successfully!');
          setSubmitSuccess(true);
          setTimeout(() => {
            setSubmitSuccess(false);
          }, 3000);
        } catch (error) {
          console.error('Error updating milk rate:', error);
          // Revert to the previous rate
          setCustomRate(currentRate.toString());
        } finally {
          setSubmitting(false);
        }
      } else {
        // If invalid, revert to current rate
        setCustomRate(currentRate.toString());
      }
    }
    setIsEditingRate(!isEditingRate);
  };

  // Handle custom rate input change
  const handleRateChange = (e) => {
    setCustomRate(e.target.value);
  };

  // Handle rate type toggle and update
  const toggleRateType = async () => {
    if (isEditingRateType) {
      try {
        setSubmittingRateType(true);
        
        // Prepare data for updating the dairy info
        const dairyData = {
          id: dairyInfo?.id,
          dairy_name: dairyInfo?.dairy_name || '',
          dairy_address: dairyInfo?.dairy_address || '',
          rate_type: rateType
        };
        
        // Call API to update the rate type
        await updateDairyInfo(dairyData);
        
        // Refresh dairy info after update
        const updatedDairyInfo = await getDairyInfo();
        setDairyInfo(updatedDairyInfo);
        
        setSubmitSuccess(true);
        setCurrentSubmitMessage('Rate type updated successfully!');
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 3000);
      } catch (error) {
        console.error('Error updating rate type:', error);
      } finally {
        setSubmittingRateType(false);
        setIsEditingRateType(false);
      }
    } else {
      setIsEditingRateType(true);
    }
  };

  // Handle rate type selection change
  const handleRateTypeChange = (e) => {
    const newRateType = e.target.value;
    setRateType(newRateType);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="logo-container">
          <img src="/Dudhiya-logo.png" alt="Dudhiya" className="logo" />
        </div>
        <h1>Dudhiya: Milk Management System</h1>
        <div className="user-info">
          <span className="user-name">{user?.name || 'User'}</span>
          <button className="logout-button" onClick={handleLogout}>
            <FontAwesomeIcon icon={faSignOutAlt} />
            <span>Logout</span>
          </button>
        </div>
      </header>

      <main className="dashboard-content">
        <section className="welcome-section">
          <h2>Welcome, {user?.name || 'User'}!</h2>
          <p>Phone: {user?.phone_number || 'Not available'}</p>
        </section>

        {/* Dairy and Owner Information Section */}
        <div className="dairy-info-container">
          {loadingInfo ? (
            <div className="info-loading">Loading information...</div>
          ) : (
            <div className="dairy-info-content">
              <div className="dairy-details">
                <div className="info-item">
                  <FontAwesomeIcon icon={faStore} className="info-icon" />
                  <span className="info-label">Dairy Name:</span>
                  <span className="info-value">{dairyInfo?.dairy_name || 'Not Available'}</span>
                </div>
                <div className="info-item">
                  <FontAwesomeIcon icon={faWallet} className="info-icon" />
                  <span className="info-label">Wallet Balance:</span>
                  <span className="info-value">
                    {walletBalance !== null && walletBalance !== undefined 
                      ? `₹${parseFloat(walletBalance).toFixed(2)}` 
                      : 'Not Available'}
                  </span>
                </div>
              </div>
              <div className="owner-details">
                <div className="info-item">
                  <FontAwesomeIcon icon={faUser} className="info-icon" />
                  <span className="info-label">Owner:</span>
                  <span className="info-value">{user?.name || 'Not Available'}</span>
                </div>
                <div className="info-item">
                  <FontAwesomeIcon icon={faPhone} className="info-icon" />
                  <span className="info-label">Phone:</span>
                  <span className="info-value">{user?.phone_number || 'Not Available'}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Current Rate Information Section */}
        <div className="rate-info-container">
          {isLoadingRate ? (
            <div className="info-loading">Loading rate information...</div>
          ) : (
            <div className="rate-info-content">
              <div className="rate-section">
                {isEditingRate ? (
                  <div className="rate-edit">
                    <label htmlFor="rate-input">Milk Rate (₹): </label>
                    <input
                      id="rate-input"
                      type="number"
                      step="0.1"
                      min="0"
                      value={customRate}
                      onChange={handleRateChange}
                      className="rate-input"
                      autoFocus
                      disabled={submitting}
                    />
                    <button 
                      type="button" 
                      onClick={toggleRateEdit}
                      className="save-rate-button"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} spin />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faCheck} />
                          <span>Save</span>
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="rate-display">
                    <p>Current Milk Rate: ₹{currentRate || '0'}</p>
                    <button 
                      type="button" 
                      onClick={toggleRateEdit}
                      className="edit-rate-button"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                      <span>Edit Rate</span>
                    </button>
                  </div>
                )}
              </div>
              
              {/* Rate Type Edit Section */}
              <div className="rate-type-section">
                {isEditingRateType ? (
                  <div className="rate-type-edit">
                    <label htmlFor="rate-type-select">Rate Type: </label>
                    <select
                      id="rate-type-select"
                      value={rateType}
                      onChange={handleRateTypeChange}
                      className="rate-type-select"
                      disabled={submittingRateType}
                    >
                      <option value="fat_snf">FAT + SNF</option>
                      <option value="fat_clr">FAT + CLR</option>
                    </select>
                    <button 
                      type="button" 
                      onClick={toggleRateType}
                      className="save-rate-button"
                      disabled={submittingRateType}
                    >
                      {submittingRateType ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} spin />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faCheck} />
                          <span>Save</span>
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="rate-type-display">
                    <p>Current Rate Type: {rateType === 'fat_snf' ? 'FAT + SNF' : 'FAT + CLR'}</p>
                    <button 
                      type="button" 
                      onClick={toggleRateType}
                      className="edit-rate-button"
                    >
                      <FontAwesomeIcon icon={faExchangeAlt} />
                      <span>Change Type</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Success message for updates */}
        {submitSuccess && (
          <div className="success-message">
            <FontAwesomeIcon icon={faCheck} />
            <span>{currentSubmitMessage}</span>
          </div>
        )}

        {/* Low Balance Alert */}
        {showLowBalanceAlert && (
          <div className="low-balance-alert">
            <FontAwesomeIcon icon={faExclamationCircle} className="alert-icon" />
            <span>Your wallet balance is below ₹50. Please add funds to ensure uninterrupted service.</span>
          </div>
        )}

        <div className="dashboard-sections">
          <div className="dashboard-section">
            <div className="section-header">
              <FontAwesomeIcon icon={faBoxOpen} className="section-icon" />
              <h3>Standard Collections</h3>
            </div>
            <div className="actions-grid">
              <Link to="/bulk-collection-v1" className="action-card primary">
                <FontAwesomeIcon icon={faPlus} className="action-icon" />
                <span>Bulk Collection</span>
              </Link>
              
              <Link to="/preview-collections" className="action-card">
                <FontAwesomeIcon icon={faListAlt} className="action-icon" />
                <span>View Collections</span>
              </Link>
            </div>
          </div>
          
          <div className="dashboard-section">
            <div className="section-header">
              <FontAwesomeIcon icon={faFileAlt} className="section-icon" />
              <h3>Raw Collections</h3>
            </div>
            <div className="actions-grid">
              <Link to="/add-raw-collection" className="action-card raw">
                <FontAwesomeIcon icon={faPlus} className="action-icon" />
                <span>Raw Collection</span>
              </Link>
              
              <Link to="/preview-raw-collections" className="action-card raw">
                <FontAwesomeIcon icon={faListAlt} className="action-icon" />
                <span>View Raw Collections</span>
              </Link>
            </div>
          </div>

          <div className="dashboard-section">
            <div className="section-header">
              <FontAwesomeIcon icon={faBoxOpen} className="section-icon" />
              <h3>Pro Rata Collections</h3>
            </div>
            <div className="actions-grid">
              <Link to="/pro-rata-collection" className="action-card primary">
                <FontAwesomeIcon icon={faPlus} className="action-icon" />
                <span>Bulk Collection</span>
              </Link>
            </div>
          </div>

        </div>
      </main>
      
      <footer className="dashboard-footer">
        <p>&copy; 2025 Dudhiya. All rights reserved.</p>
        <img src="/Powered-By-Netpy-Technologies.png" style={{width: '170px'}} alt="Powered by Netpy Technologies" />
      </footer>
    </div>
  );
};

export default Dashboard; 
